import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import api from '../api';
import OfferDeskNav from '../components/OfferDeskNav';
import { applyModeLabel, portalLabel } from '../utils/applyDisclosure';

/* ─── Config ─────────────────────────────────────────────── */
const STATUS_OPTIONS = ['saved', 'needs_info', 'prepared', 'handoff_started', 'submitted', 'submission_failed', 'interview', 'offer', 'rejected'];

const STATUS_CONFIG = {
  saved:             { bg:'#EEF2F4', color:'#475569', border:'#D9E2E6', dot:'#94A3B8',  label:'Saved',           accent:'#64748B' },
  needs_info:        { bg:'#FFF7ED', color:'#9A5809', border:'#F5D9B6', dot:'#F59E0B',  label:'Needs info',      accent:'#F59E0B' },
  prepared:          { bg:'#EEF2FF', color:'#4338CA', border:'#C7D2FE', dot:'#4338CA',  label:'Prepared',        accent:'#4338CA' },
  handoff_started:   { bg:'#FEF3C7', color:'#92400E', border:'#FDE68A', dot:'#D97706',  label:'Fast apply',      accent:'#D97706' },
  submitted:         { bg:'#ECFDF5', color:'#065F46', border:'#A7F3D0', dot:'#10B981',  label:'Submitted',       accent:'#10B981' },
  submission_failed: { bg:'#FFF0F0', color:'#991B1B', border:'#FECACA', dot:'#EF4444',  label:'Submission failed', accent:'#EF4444' },
  applied:           { bg:'#EEF2FF', color:'#4338CA', border:'#C7D2FE', dot:'#4338CA',  label:'Applied',         accent:'#4338CA' },
  interview:         { bg:'#FFFBEB', color:'#92400E', border:'#FDE68A', dot:'#F59E0B',  label:'Interview',       accent:'#F59E0B' },
  offer:             { bg:'#ECFDF5', color:'#065F46', border:'#A7F3D0', dot:'#10B981',  label:'Offer',           accent:'#10B981' },
  rejected:          { bg:'#FFF0F0', color:'#991B1B', border:'#FECACA', dot:'#EF4444',  label:'Rejected',        accent:'#EF4444' },
};

/* ─── Icons ──────────────────────────────────────────────── */
const Ic = {
  arrow:  () => <svg width="12" height="12" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7"/></svg>,
  x:      () => <svg width="13" height="13" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"/></svg>,
  empty:  () => <svg width="26" height="26" fill="none" stroke="#94A3B8" viewBox="0 0 24 24" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>,
  jobs:   () => <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg>,
  check:  () => <svg width="13" height="13" fill="none" stroke="#4E6C7E" viewBox="0 0 24 24" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/></svg>,
  alert:  () => <svg width="13" height="13" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><circle cx="12" cy="12" r="10"/><path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01"/></svg>,
};

/* ─── FadeIn ─────────────────────────────────────────────── */
function FadeIn({ children, delay = 0, y = 16, style }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-40px' });
  return (
    <motion.div ref={ref} style={style} initial={{ opacity: 0, y }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, delay, ease: [0.22, 1, 0.36, 1] }}>
      {children}
    </motion.div>
  );
}

/* ─── Toast ──────────────────────────────────────────────── */
function Toast({ toast }) {
  return (
    <AnimatePresence>
      {toast && (
        <motion.div
          initial={{ opacity: 0, y: 16, x: '-50%' }}
          animate={{ opacity: 1, y: 0, x: '-50%' }}
          exit={{ opacity: 0, y: 8, x: '-50%' }}
          style={{
            position: 'fixed', bottom: 28, left: '50%',
            padding: '11px 20px', borderRadius: 100, fontSize: 13, fontWeight: 500,
            zIndex: 999, whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 8,
            boxShadow: '0 8px 28px rgba(0,0,0,0.15)', fontFamily: 'var(--tandm-font-sans)',
            background: toast.type === 'error' ? '#FFF0F0' : '#0B1220',
            color: toast.type === 'error' ? '#D94040' : '#fff',
            border: toast.type === 'error' ? '1px solid #FECACA' : '1px solid rgba(255,255,255,0.1)',
          }}
        >
          {toast.type === 'error' ? <Ic.alert /> : <Ic.check />}
          {toast.msg}
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/* ─── Stat Card ──────────────────────────────────────────── */
function StatCard({ status, count, active, onClick, delay }) {
  const cfg = STATUS_CONFIG[status];
  return (
    <FadeIn delay={delay} style={{ height: '100%', display: 'flex' }}>
      <motion.div
        whileHover={{ y: -4, boxShadow: `0 8px 24px ${cfg.accent}22` }}
        whileTap={{ scale: 0.97 }}
        onClick={onClick}
        style={{
          background: active ? cfg.bg : 'rgba(255,255,255,0.78)',
          backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)',
          border: `1.5px solid ${active ? cfg.border : 'rgba(255,255,255,0.95)'}`,
          borderRadius: 16, padding: '14px 16px', cursor: 'pointer',
          boxShadow: active ? `0 4px 16px ${cfg.accent}18` : '0 2px 8px rgba(0,0,0,0.04)',
          transition: 'border-color 0.2s, background 0.2s',
          flex: 1,
          minHeight: 96,
          display: 'flex',
          flexDirection: 'column',
          boxSizing: 'border-box',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 5, marginBottom: 8, minHeight: 32 }}>
          <motion.span
            animate={{ scale: active ? [1, 1.3, 1] : 1 }}
            transition={{ duration: 0.3 }}
            style={{ width: 7, height: 7, borderRadius: '50%', background: cfg.dot, flexShrink: 0, marginTop: 2 }}
          />
          <span style={{ fontSize: 10, fontWeight: 600, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.04em', lineHeight: 1.3 }}>{cfg.label}</span>
        </div>
        <p style={{ fontSize: 24, fontWeight: 800, color: count > 0 ? cfg.color : '#94A3B8', letterSpacing: '-0.04em', margin: 0, marginTop: 'auto', lineHeight: 1 }}>{count}</p>
      </motion.div>
    </FadeIn>
  );
}

/* ─── Application Row ────────────────────────────────────── */
function AppRow({ app, onStatusChange, onRemove, index }) {
  const cfg = STATUS_CONFIG[app.status] || STATUS_CONFIG.saved;
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.97 }}
      transition={{ duration: 0.35, delay: index * 0.04, ease: [0.22, 1, 0.36, 1] }}
      whileHover={{ x: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.07)' }}
      style={{
        background: 'rgba(255,255,255,0.8)',
        backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)',
        border: '1px solid rgba(255,255,255,0.95)',
        borderRadius: 16, padding: '14px 16px',
        display: 'flex', alignItems: 'center', gap: 13,
        boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
      }}
    >
      {/* Avatar */}
      <div style={{
        width: 38, height: 38, borderRadius: 11, flexShrink: 0,
        background: `linear-gradient(135deg,${cfg.bg},${cfg.color}22)`,
        border: `1px solid ${cfg.border}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 14, fontWeight: 800, color: cfg.color,
      }}>
        {app.company_name?.charAt(0)}
      </div>

      {/* Info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <Link
          to={`/jobs/${app.internship_id}`}
          style={{ fontSize: 13, fontWeight: 600, color: '#0B1220', textDecoration: 'none', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', transition: 'color 0.15s' }}
          onMouseEnter={e => e.currentTarget.style.color = '#4E6C7E'}
          onMouseLeave={e => e.currentTarget.style.color = '#0B1220'}
        >
          {app.title}
        </Link>
        <p style={{ fontSize: 11, color: '#94A3B8', margin: '2px 0 0' }}>{app.company_name}</p>
        {(app.application_mode || app.portal_type || app.failure_reason) && (
          <p style={{ fontSize: 11, color: app.failure_reason ? 'var(--tandm-danger-fg)' : 'var(--tandm-muted)', margin: '4px 0 0', lineHeight: 1.5 }}>
            {[
              app.application_mode ? applyModeLabel(app.application_mode) : null,
              portalLabel(app.portal_type),
            ].filter(Boolean).join(' · ')}
            {app.failure_reason ? `${app.application_mode || app.portal_type ? ' · ' : ''}${app.failure_reason}` : ''}
          </p>
        )}
      </div>

      {/* Status dropdown */}
      <select
        value={app.status}
        onChange={e => onStatusChange(app.id, e.target.value)}
        style={{
          fontSize: 11, fontWeight: 600, padding: '5px 10px', borderRadius: 100,
          border: `1px solid ${cfg.border}`, cursor: 'pointer', outline: 'none',
          fontFamily: 'var(--tandm-font-sans)', background: cfg.bg, color: cfg.color,
          appearance: 'none', WebkitAppearance: 'none', textAlign: 'center', flexShrink: 0,
        }}
      >
        {STATUS_OPTIONS.map(s => <option key={s} value={s}>{STATUS_CONFIG[s].label}</option>)}
      </select>

      {/* View link */}
      <Link
        to={`/jobs/${app.internship_id}`}
        style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#94A3B8', textDecoration: 'none', flexShrink: 0, transition: 'color 0.15s' }}
        onMouseEnter={e => e.currentTarget.style.color = '#4E6C7E'}
        onMouseLeave={e => e.currentTarget.style.color = '#94A3B8'}
      >
        View <Ic.arrow />
      </Link>

      {/* Remove */}
      <motion.button
        whileHover={{ background: '#FFF0F0', color: '#D94040' }}
        whileTap={{ scale: 0.9 }}
        onClick={() => onRemove(app.id)}
        style={{ width: 28, height: 28, borderRadius: 8, border: 'none', background: 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94A3B8', flexShrink: 0, transition: 'all 0.15s' }}
      >
        <Ic.x />
      </motion.button>
    </motion.div>
  );
}

/* ─── Main ───────────────────────────────────────────────── */
export default function Applications() {
  const { user } = useAuth();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [filter, setFilter] = useState('all');

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 2500);
  };

  useEffect(() => {
    document.documentElement.style.scrollBehavior = 'smooth';
    fetchApplications();
  }, []);

  const fetchApplications = async () => {
    try {
      const res = await api.get('/applications/my');
      setApplications(res.data.applications || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const updateStatus = async (id, status) => {
    try {
      await api.put(`/applications/${id}/status`, { status });
      setApplications(prev => prev.map(a => a.id === id ? { ...a, status } : a));
      showToast(`Moved to ${STATUS_CONFIG[status].label}`);
    } catch { showToast('Failed to update', 'error'); }
  };

  const removeApplication = async (id) => {
    try {
      await api.delete(`/applications/${id}`);
      setApplications(prev => prev.filter(a => a.id !== id));
      showToast('Removed');
    } catch { showToast('Failed to remove', 'error'); }
  };

  const counts = STATUS_OPTIONS.reduce((acc, s) => {
    acc[s] = applications.filter(a => a.status === s).length;
    return acc;
  }, {});

  const visible = filter === 'all' ? applications : applications.filter(a => a.status === filter);
  const grouped = STATUS_OPTIONS.reduce((acc, s) => {
    acc[s] = visible.filter(a => a.status === s);
    return acc;
  }, {});

  if (loading) return (
    <div style={{ minHeight: '100vh', background: '#F4F6F7', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--tandm-font-sans)' }}>
      <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 }}>
        <div style={{ width: 36, height: 36, border: '3px solid #EEF2F4', borderTopColor: '#4E6C7E', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
        <p style={{ fontSize: 13, color: '#94A3B8', fontWeight: 500 }}>Loading applications...</p>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: '#F4F6F7', fontFamily: 'var(--tandm-font-sans)' }}>
      <style>{`
        /* fonts via index.css tokens */
        @keyframes spin { to { transform: rotate(360deg) } }
        @keyframes gradientShift { 0%{background-position:0% 50%} 50%{background-position:100% 50%} 100%{background-position:0% 50%} }
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.3} }
        * { box-sizing: border-box; }
        ::-webkit-scrollbar { width: 6px; }
        ::-webkit-scrollbar-thumb { background: rgba(78,108,126,0.35); border-radius: 3px; }
      `}</style>

      <OfferDeskNav />

      {/* HERO */}
      <div style={{
        background: 'var(--tandm-gradient-hero-dark)',
        paddingTop: 'var(--tandm-nav-clearance, 112px)',
        paddingLeft: 24, paddingRight: 24, paddingBottom: 52,
        position: 'relative', overflow: 'hidden',
      }}>
        <div style={{
          position: 'absolute', inset: 0, opacity: 0.04, pointerEvents: 'none',
          backgroundImage: 'linear-gradient(rgba(255,255,255,0.5) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,0.5) 1px,transparent 1px)',
          backgroundSize: '36px 36px',
        }} />
        <motion.div
          animate={{ scale: [1, 1.12, 1], opacity: [0.08, 0.13, 0.08] }}
          transition={{ duration: 9, repeat: Infinity, ease: 'easeInOut' }}
          style={{ position: 'absolute', width: 300, height: 300, borderRadius: '50%', background: 'radial-gradient(circle,#4E6C7E 0%,transparent 70%)', right: -40, top: -80, pointerEvents: 'none' }}
        />
        <motion.div
          animate={{ scale: [1, 1.1, 1], opacity: [0.05, 0.09, 0.05] }}
          transition={{ duration: 11, repeat: Infinity, ease: 'easeInOut', delay: 3 }}
          style={{ position: 'absolute', width: 200, height: 200, borderRadius: '50%', background: 'radial-gradient(circle,#6366F1 0%,transparent 70%)', left: '30%', bottom: -60, pointerEvents: 'none' }}
        />

        <div style={{ maxWidth: 860, margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <motion.div initial={{ opacity: 0, y: 22 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}>
            <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
              <div>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 7, background: 'rgba(78,108,126,0.15)', border: '1px solid rgba(78,108,126,0.3)', borderRadius: 100, padding: '5px 14px', marginBottom: 16 }}>
                  <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#4E6C7E', animation: 'pulse 2s ease infinite' }} />
                  <span style={{ fontSize: 11, fontWeight: 600, color: '#7CACB7', letterSpacing: '0.05em' }}>Application tracker</span>
                </div>
                <h1 style={{ fontFamily: 'var(--tandm-font-display)', fontSize: 'clamp(26px,4vw,40px)', fontWeight: 600, color: '#fff', letterSpacing: '-0.03em', lineHeight: 1.12, margin: '0 0 8px' }}>
                  My applications
                </h1>
                <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.35)', margin: 0 }}>
                  {applications.length === 0
                    ? 'Save internships to start tracking'
                    : `${applications.length} total · ${counts.applied || 0} applied · ${counts.interview || 0} interview${counts.interview !== 1 ? 's' : ''}`
                  }
                </p>
              </div>
              <motion.div whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.97 }}>
                <Link
                  to="/jobs"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(78,108,126,0.9)', color: '#fff', padding: '10px 18px', borderRadius: 12, fontSize: 13, fontWeight: 600, textDecoration: 'none', flexShrink: 0, fontFamily: 'var(--tandm-font-sans)', boxShadow: '0 4px 16px rgba(78,108,126,0.3)' }}
                >
                  <Ic.jobs /> Browse more
                </Link>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* BODY */}
      <div style={{ maxWidth: 860, margin: '0 auto', padding: '28px 24px 72px' }}>

        {applications.length === 0 ? (
          <FadeIn>
            <div style={{
              background: 'rgba(255,255,255,0.78)', backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255,255,255,0.95)', borderRadius: 24,
              padding: '72px 24px', display: 'flex', flexDirection: 'column',
              alignItems: 'center', textAlign: 'center',
              boxShadow: '0 2px 16px rgba(0,0,0,0.06)',
            }}>
              <div style={{ width: 60, height: 60, background: '#FFF0E8', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
                <Ic.empty />
              </div>
              <h3 style={{ fontSize: 17, fontWeight: 700, color: '#0B1220', margin: '0 0 8px', letterSpacing: '-0.02em' }}>No applications yet</h3>
              <p style={{ fontSize: 13, color: '#94A3B8', margin: '0 0 28px', maxWidth: 260, lineHeight: 1.65 }}>
                Save internships you want to apply to and track your progress here.
              </p>
              <motion.div whileHover={{ scale: 1.04, boxShadow: '0 8px 24px rgba(78,108,126,0.3)' }} whileTap={{ scale: 0.97 }}>
                <Link to="/jobs" style={{ display: 'inline-flex', alignItems: 'center', gap: 7, background: 'var(--tandm-gradient-cta)', color: '#fff', padding: '12px 24px', borderRadius: 12, fontSize: 13, fontWeight: 600, textDecoration: 'none', fontFamily: 'var(--tandm-font-sans)', boxShadow: '0 4px 16px rgba(78,108,126,0.25)' }}>
                  Browse internships <Ic.arrow />
                </Link>
              </motion.div>
            </div>
          </FadeIn>
        ) : (
          <>
            {/* Stat cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', gap: 10, marginBottom: 20, alignItems: 'stretch' }}>
              {STATUS_OPTIONS.map((s, i) => (
                <StatCard
                  key={s}
                  status={s}
                  count={counts[s]}
                  active={filter === s}
                  onClick={() => setFilter(filter === s ? 'all' : s)}
                  delay={i * 0.06}
                />
              ))}
            </div>

            {/* Filter pills */}
            <FadeIn delay={0.1}>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 24 }}>
                <motion.button
                  whileHover={{ y: -1 }} whileTap={{ scale: 0.95 }}
                  onClick={() => setFilter('all')}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 7, padding: '7px 14px', borderRadius: 100,
                    fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--tandm-font-sans)',
                    border: filter === 'all' ? 'none' : '1.5px solid #D9E2E6',
                    background: filter === 'all' ? '#0B1220' : 'rgba(255,255,255,0.8)',
                    color: filter === 'all' ? '#fff' : '#475569',
                    backdropFilter: 'blur(8px)',
                    boxShadow: filter === 'all' ? '0 4px 12px rgba(26,18,8,0.2)' : 'none',
                  }}
                >
                  All
                  <span style={{ fontSize: 11, background: filter === 'all' ? 'rgba(255,255,255,0.15)' : '#EEF2F4', color: filter === 'all' ? '#fff' : '#64748B', padding: '1px 7px', borderRadius: 100 }}>
                    {applications.length}
                  </span>
                </motion.button>

                {STATUS_OPTIONS.filter(s => counts[s] > 0).map(s => {
                  const cfg = STATUS_CONFIG[s];
                  const active = filter === s;
                  return (
                    <motion.button
                      key={s}
                      whileHover={{ y: -1 }} whileTap={{ scale: 0.95 }}
                      onClick={() => setFilter(filter === s ? 'all' : s)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 7, padding: '7px 14px', borderRadius: 100,
                        fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--tandm-font-sans)',
                        border: active ? 'none' : '1.5px solid #D9E2E6',
                        background: active ? cfg.bg : 'rgba(255,255,255,0.8)',
                        color: active ? cfg.color : '#475569',
                        backdropFilter: 'blur(8px)',
                        boxShadow: active ? `0 4px 12px ${cfg.accent}22` : 'none',
                      }}
                    >
                      <span style={{ width: 6, height: 6, borderRadius: '50%', background: cfg.dot }} />
                      {cfg.label}
                      <span style={{ fontSize: 11, background: 'rgba(0,0,0,0.07)', padding: '1px 7px', borderRadius: 100 }}>{counts[s]}</span>
                    </motion.button>
                  );
                })}
              </div>
            </FadeIn>

            {/* Grouped rows */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
              <AnimatePresence>
                {STATUS_OPTIONS.map(status => {
                  const apps = grouped[status];
                  if (!apps || apps.length === 0) return null;
                  const cfg = STATUS_CONFIG[status];
                  return (
                    <motion.div
                      key={status}
                      layout
                      initial={{ opacity: 0, y: 16 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.4 }}
                    >
                      {/* Section header */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 12 }}>
                        <span style={{ width: 8, height: 8, borderRadius: '50%', background: cfg.dot, flexShrink: 0 }} />
                        <span style={{ fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.07em' }}>{cfg.label}</span>
                        <span style={{ fontSize: 11, fontWeight: 600, background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`, padding: '2px 9px', borderRadius: 100 }}>{apps.length}</span>
                        <div style={{ flex: 1, height: 1, background: 'rgba(0,0,0,0.06)' }} />
                      </div>

                      {/* Rows */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        <AnimatePresence>
                          {apps.map((app, i) => (
                            <AppRow
                              key={app.id}
                              app={app}
                              index={i}
                              onStatusChange={updateStatus}
                              onRemove={removeApplication}
                            />
                          ))}
                        </AnimatePresence>
                      </div>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </div>
          </>
        )}
      </div>

      <Toast toast={toast} />
    </div>
  );
}
